/**
 * First-touch attribution capture.
 *
 * Capture the page a visitor first entered on for every visit, not only paid
 * campaign visits. Campaign parameters are included when present so a quote
 * request can be tied back to the landing page that generated it.
 *
 * sessionStorage is deliberate: this describes how the current visit started,
 * not how someone found the site weeks ago.
 */

const STORAGE_KEY = "401pc:attribution"

const TRACKED_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
  "gclid",
  "fbclid",
]

const readStore = () => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (e) {
    return null
  }
}

/**
 * Record the first page of the current visit plus any campaign parameters.
 * Called from gatsby-browser's onClientEntry.
 */
export const captureAttribution = () => {
  if (typeof window === "undefined") return
  if (readStore()) return

  const params = new URLSearchParams(window.location.search)
  const captured = {
    landing_page: window.location.pathname || "/",
  }

  TRACKED_PARAMS.forEach(key => {
    const value = params.get(key)
    if (value) captured[key] = value
  })

  // Preserve only an external referrer. Internal navigation is already
  // represented by landing_page and does not help explain acquisition.
  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer)
      if (referrer.origin !== window.location.origin) {
        captured.referrer = referrer.href
      }
    } catch (e) {
      // Ignore malformed referrers.
    }
  }

  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured))
  } catch (e) {
    // Storage unavailable — attribution must never break the site.
  }
}

const describeSource = data => {
  const source =
    data.utm_source ||
    (data.gclid ? "google" : "") ||
    (data.fbclid ? "facebook" : "")

  if (source) {
    const detail = []
    if (data.utm_campaign) detail.push(data.utm_campaign)
    if (data.utm_content) detail.push(`ad ${data.utm_content}`)
    const medium = data.utm_medium ? ` (${data.utm_medium})` : ""
    const suffix = detail.length ? ` — ${detail.join(", ")}` : ""
    return `${source}${medium}${suffix}`
  }

  if (data.referrer) {
    try {
      return `Referral — ${new URL(data.referrer).hostname}`
    } catch (e) {
      return "Referral"
    }
  }

  return "Direct or organic"
}

/**
 * Return first-touch context in a form-ready shape.
 */
export const getAttribution = () => {
  if (typeof window === "undefined") return null

  const data = readStore()
  if (!data) return null

  return {
    lead_source: describeSource(data),
    landing_page: data.landing_page || "/",
    referrer: data.referrer || "",
    attribution_detail: Object.entries(data)
      .map(([key, value]) => `${key}=${value}`)
      .join("&"),
  }
}
